import * as THREE from 'three';
import { collectWindowObjects } from './buildingWindows';
import { useWindowSettingsStore } from '@/store/windowSettingsStore';

export function snapToGrid(
	point: THREE.Vector3,
	wallMesh: THREE.Mesh,
	face: THREE.Face,
	tangent: THREE.Vector3,
	bitangent: THREE.Vector3,
	normal: THREE.Vector3,
	windowWidth: number,
	windowHeight: number
): THREE.Vector3 {
	const gridSize = useWindowSettingsStore.getState().snapPointDistance;
	const safeGridSize = Number.isFinite(gridSize) && gridSize > 0 ? gridSize : 0;
	const geom = wallMesh.geometry;
	const posAttr = geom.getAttribute('position');
	const va = new THREE.Vector3().fromBufferAttribute(posAttr, face.a);
	const vb = new THREE.Vector3().fromBufferAttribute(posAttr, face.b);
	const vc = new THREE.Vector3().fromBufferAttribute(posAttr, face.c);
	
	let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
	for (const vertex of [va, vb, vc]) {
		const localX = vertex.dot(tangent);
		const localY = vertex.dot(bitangent);
		minX = Math.min(minX, localX);
		maxX = Math.max(maxX, localX);
		minY = Math.min(minY, localY);
		maxY = Math.max(maxY, localY);
	}
	
	const localX = point.dot(tangent);
	const localY = point.dot(bitangent);
	const localZ = point.dot(normal);
	
	const windowLeftEdge = localX - windowWidth / 2;
	const windowBottomEdge = localY - windowHeight / 2;
	
	const edgeOffsetX = windowLeftEdge - minX;
	const edgeOffsetY = windowBottomEdge - minY;

	const snappedEdgeOffsetX =
		safeGridSize > 0 ? Math.round(edgeOffsetX / safeGridSize) * safeGridSize : edgeOffsetX;
	const snappedEdgeOffsetY =
		safeGridSize > 0 ? Math.round(edgeOffsetY / safeGridSize) * safeGridSize : edgeOffsetY;
	
	const wallWidth = maxX - minX;
	const wallHeight = maxY - minY;
	const clampedEdgeOffsetX = Math.max(0, Math.min(snappedEdgeOffsetX, wallWidth - windowWidth));
	const clampedEdgeOffsetY = Math.max(0, Math.min(snappedEdgeOffsetY, wallHeight - windowHeight));
	
	const snappedWindowLeftEdge = minX + clampedEdgeOffsetX;
	const snappedWindowBottomEdge = minY + clampedEdgeOffsetY;
	
	const snappedX = snappedWindowLeftEdge + windowWidth / 2;
	const snappedY = snappedWindowBottomEdge + windowHeight / 2;
	
	const snappedPoint = new THREE.Vector3();
	snappedPoint.addScaledVector(tangent, snappedX);
	snappedPoint.addScaledVector(bitangent, snappedY);
	snappedPoint.addScaledVector(normal, localZ);
	
	return snappedPoint;
}
let hoveredWindowMesh: THREE.Group | null = null;
const originalWindowMaterials = new WeakMap<THREE.Mesh, THREE.Material | THREE.Material[]>();

export function updateWindowDeletionHighlight(
	scene: THREE.Scene,
	camera: THREE.Camera,
	mouse: THREE.Vector2,
	isHoleDeletionMode: boolean
): void {
	if (!isHoleDeletionMode) {
		if (hoveredWindowMesh) {
			restoreWindowMaterials(hoveredWindowMesh);
			hoveredWindowMesh = null;
		}
		return;
	}

	const raycaster = new THREE.Raycaster();
	raycaster.setFromCamera(mouse, camera);

	const windowObjects = collectWindowObjects(scene);
	const intersects = raycaster.intersectObjects(windowObjects, true);

	let newHoveredWindow: THREE.Group | null = null;

	if (intersects.length > 0) {
		let currentObj = intersects[0].object;

		while (currentObj) {
			if (currentObj.userData.isWindow) {
				newHoveredWindow = currentObj as THREE.Group;
				break;
			}
			if (!currentObj.parent || currentObj.parent === scene) {
				break;
			}
			currentObj = currentObj.parent;
		}
	}

	if (newHoveredWindow !== hoveredWindowMesh) {
		if (hoveredWindowMesh) {
			restoreWindowMaterials(hoveredWindowMesh);
		}

		if (newHoveredWindow) {
			highlightWindow(newHoveredWindow);
		}

		hoveredWindowMesh = newHoveredWindow;
	}
}

export function highlightWindow(windowGroup: THREE.Group): void {
	windowGroup.traverse((child) => {
		if (child instanceof THREE.Mesh) {
			if (!originalWindowMaterials.has(child)) {
				originalWindowMaterials.set(child, child.material);
			}

			const highlightMaterial = Array.isArray(child.material)
				? child.material.map((mat) => mat.clone())
				: child.material.clone();

			if (Array.isArray(highlightMaterial)) {
				highlightMaterial.forEach((mat) => {
					if ('emissive' in mat) {
						mat.emissive = new THREE.Color(0xff0000);
						mat.emissiveIntensity = 0.5;
					}
					if ('color' in mat) {
						mat.color = new THREE.Color(0xff6666);
					}
				});
			} else {
				if ('emissive' in highlightMaterial) {
					highlightMaterial.emissive = new THREE.Color(0xff0000);
					highlightMaterial.emissiveIntensity = 0.5;
				}
				if ('color' in highlightMaterial) {
					highlightMaterial.color = new THREE.Color(0xff6666);
				}
			}

			child.material = highlightMaterial;
			useWindowSettingsStore.getState().setHighlightedWindowUUID(windowGroup.userData.windowUuid);
		}
	});
}

export function restoreWindowMaterials(windowGroup: THREE.Group): void {
	windowGroup.traverse((child) => {
		if (child instanceof THREE.Mesh) {
			const originalMaterial = originalWindowMaterials.get(child);
			if (originalMaterial) {
				if (Array.isArray(child.material)) {
					child.material.forEach((mat) => mat.dispose());
				} else {
					child.material.dispose();
				}

				child.material = originalMaterial;
				useWindowSettingsStore.getState().setHighlightedWindowUUID(null);
			}
		}
	});
}

export function highlightWindowOnPoint(point: string, scene: THREE.Scene | null): void {
	if (!scene) return;

	const pointObject = point.split(',');
	if (pointObject.length !== 3) return;

	const targetX = parseFloat(pointObject[0]);
	const targetY = parseFloat(pointObject[1]);
	const targetZ = parseFloat(pointObject[2]);

	// Use a small epsilon for floating point comparison
	const epsilon = 0.05;

	scene.traverse((obj) => {
		if (!obj.userData.isWindow) return;

		const matchesX = Math.abs(obj.position.x - targetX) < epsilon;
		const matchesY = Math.abs(obj.position.y - targetY) < epsilon;
		const matchesZ = Math.abs(obj.position.z - targetZ) < epsilon;

		if (matchesX && matchesY && matchesZ) {
			if (obj instanceof THREE.Group) {
				highlightWindow(obj);
			}
		}
	});
}

export function restoreWindowOnPoint(point: string, scene: THREE.Scene | null): void {
	if (!scene) return;

	const pointObject = point.split(',');
	if (pointObject.length !== 3) return;

	const targetX = parseFloat(pointObject[0]);
	const targetY = parseFloat(pointObject[1]);
	const targetZ = parseFloat(pointObject[2]);

	// Use a small epsilon for floating point comparison
	const epsilon = 0.05;

	scene.traverse((obj) => {
		if (!obj.userData.isWindow) return;

		const matchesX = Math.abs(obj.position.x - targetX) < epsilon;
		const matchesY = Math.abs(obj.position.y - targetY) < epsilon;
		const matchesZ = Math.abs(obj.position.z - targetZ) < epsilon;

		if (matchesX && matchesY && matchesZ) {
			if (obj instanceof THREE.Group) {
				restoreWindowMaterials(obj);
			}
		}
	});
}
